import { useMemo, useState } from 'react';
import axios from 'axios';
import { Calculator, Copy, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { apiUrl, formatApiErrorDetail } from '@/lib/apiBase';

const DISCLAIMER =
  'Stima basata sulle Tabelle del Tribunale di Milano 2024. Non sostituisce una consulenza medico-legale (CTU).';

function formatEuro(value) {
  return Number(value ?? 0).toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
  });
}

function formatPct(value) {
  return Number(value ?? 0).toLocaleString('it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function clampInt(raw, min, max, fallback = min) {
  const n = Number.parseInt(String(raw), 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function clampFloat(raw, min, max, fallback = min) {
  const n = Number.parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fallback */
    }
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(el);
  return ok;
}

function buildProspetto(result) {
  if (!result) return '';
  const t = result.dettaglio_temporaneo || {};
  const itt = t.itt_100 || {};
  const itp75 = t.itp_75 || {};
  const itp50 = t.itp_50 || {};
  const itp25 = t.itp_25 || {};
  return [
    'PROSPETTO STIMA DANNO BIOLOGICO NON PATRIMONIALE',
    `Tabelle: ${result.tabelle || 'Milano 2024'}`,
    result.fonte ? `Fonte: ${result.fonte}` : null,
    '',
    `Età vittima: ${result.eta_vittima} anni`,
    `Invalidità permanente: ${formatPct(result.percentuale_invalidita)}%`,
    `Punto biologico (età 1): ${formatEuro(result.punto_biologico_eta_riferimento)}`,
    `Coefficiente età: ${Number(result.coefficiente_eta).toLocaleString('it-IT', { minimumFractionDigits: 3, maximumFractionDigits: 6 })}`,
    '',
    `Danno biologico permanente base: ${formatEuro(result.danno_biologico_permanente_base)}`,
    `Incremento sofferenza morale (${formatPct(result.percentuale_danno_morale)}%): ${formatEuro(result.incremento_sofferenza_morale)}`,
    `Permanente comprensivo di morale: ${formatEuro(result.danno_permanente_comprensivo_morale)}`,
    '',
    `Danno biologico temporaneo (ITT € ${Number(result.valore_giornaliero_itt_100).toLocaleString('it-IT')}/giorno): ${formatEuro(result.danno_biologico_temporaneo)}`,
    `  - ITT 100%: ${itt.giorni || 0} gg → ${formatEuro(itt.importo)}`,
    `  - ITP 75%: ${itp75.giorni || 0} gg → ${formatEuro(itp75.importo)}`,
    `  - ITP 50%: ${itp50.giorni || 0} gg → ${formatEuro(itp50.importo)}`,
    `  - ITP 25%: ${itp25.giorni || 0} gg → ${formatEuro(itp25.importo)}`,
    `  - Giorni ITT equivalenti: ${result.giorni_itt_equivalenti}`,
    '',
    `TOTALE GENERALE STIMATO: ${formatEuro(result.totale_stimato)}`,
    '',
    DISCLAIMER,
  ]
    .filter((line) => line !== null)
    .join('\n');
}

function NumberField({ id, label, hint, value, onChange, min, max, step = 1 }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm" style={{ color: '#E2E8F0' }}>
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-luxury"
        style={{ backgroundColor: '#0A192F', borderColor: 'rgba(255,255,255,0.12)', color: '#F8F9FA' }}
        data-testid={id}
      />
      {hint ? (
        <p className="text-xs" style={{ color: '#64748B' }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function DannoBiologicoCalculator() {
  const [eta, setEta] = useState(35);
  const [invalidita, setInvalidita] = useState(3);
  const [morale, setMorale] = useState(25);
  const [itt, setItt] = useState(0);
  const [itp75, setItp75] = useState(0);
  const [itp50, setItp50] = useState(0);
  const [itp25, setItp25] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const sliderClass =
    '[&_[role=slider]]:border-[#D4AF37] [&_[role=slider]]:bg-[#F8F9FA] [&>span:first-child>span]:bg-[#D4AF37] [&>span:first-child]:bg-slate-700';

  const payload = useMemo(
    () => ({
      eta_vittima: clampInt(eta, 1, 100, 1),
      percentuale_invalidita: clampFloat(invalidita, 1, 100, 1),
      percentuale_danno_morale: clampFloat(morale, 0, 50, 25),
      giorni_inabilita_totale: clampInt(itt, 0, 3650, 0),
      giorni_inabilita_parziale_75: clampInt(itp75, 0, 3650, 0),
      giorni_inabilita_parziale_50: clampInt(itp50, 0, 3650, 0),
      giorni_inabilita_parziale_25: clampInt(itp25, 0, 3650, 0),
    }),
    [eta, invalidita, morale, itt, itp75, itp50, itp25]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setCopied(false);
    setIsCalculating(true);
    try {
      const token = String(window.localStorage.getItem('leadlegale_token') || '').trim();
      const { data } = await axios({
        method: 'POST',
        url: apiUrl('/api/calculator/danno-biologico'),
        data: payload,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setResult(data);
    } catch (err) {
      setResult(null);
      const status = err?.response?.status;
      const detail = formatApiErrorDetail(err?.response?.data?.detail);
      if (status === 401) {
        setError(detail || 'Sessione scaduta. Effettua di nuovo l\'accesso.');
      } else if (status === 403) {
        setError(detail || 'Accesso riservato agli studi partner.');
      } else if (status === 405) {
        setError('Endpoint calcolatore non disponibile (405). Aggiorna la pagina dopo il deploy.');
      } else {
        setError(detail || 'Calcolo non riuscito. Riprova.');
      }
    } finally {
      setIsCalculating(false);
    }
  };

  const handleCopy = async () => {
    const text = buildProspetto(result);
    if (!text) return;
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6" data-testid="danno-biologico-calculator">
      <div
        className="rounded-xl p-5 md:p-6"
        style={{ backgroundColor: '#112240', border: '1px solid rgba(212, 175, 55, 0.28)' }}
      >
        <div className="flex items-start gap-3 mb-5">
          <Calculator className="w-6 h-6 mt-0.5 shrink-0" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
          <div>
            <h2 className="text-lg md:text-xl font-medium" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
              Calcolatore danno biologico
            </h2>
            <p className="text-sm mt-1" style={{ color: '#A0ABC0' }}>
              Stima orientativa permanente + morale + inabilità temporanea (Tabelle Milano 2024).
            </p>
          </div>
        </div>

        <form method="post" action="/api/calculator/danno-biologico" onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="calc-eta" style={{ color: '#E2E8F0' }}>
                  Età della vittima
                </Label>
                <span className="text-sm font-semibold tabular-nums" style={{ color: '#D4AF37' }}>
                  {payload.eta_vittima} anni
                </span>
              </div>
              <Slider
                id="calc-eta"
                min={1}
                max={100}
                step={1}
                value={[payload.eta_vittima]}
                onValueChange={(v) => setEta(v[0])}
                className={sliderClass}
                data-testid="calc-eta-slider"
              />
              <Input
                type="number"
                min={1}
                max={100}
                value={eta}
                onChange={(e) => setEta(e.target.value)}
                onBlur={() => setEta(payload.eta_vittima)}
                className="input-luxury"
                style={{ backgroundColor: '#0A192F', borderColor: 'rgba(255,255,255,0.12)', color: '#F8F9FA' }}
                data-testid="calc-eta-input"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="calc-ip" style={{ color: '#E2E8F0' }}>
                  Invalidità permanente
                </Label>
                <span className="text-sm font-semibold tabular-nums" style={{ color: '#D4AF37' }}>
                  {formatPct(payload.percentuale_invalidita)}%
                </span>
              </div>
              <Slider
                id="calc-ip"
                min={1}
                max={100}
                step={0.5}
                value={[payload.percentuale_invalidita]}
                onValueChange={(v) => setInvalidita(v[0])}
                className={sliderClass}
                data-testid="calc-ip-slider"
              />
              <Input
                type="number"
                min={1}
                max={100}
                step={0.5}
                value={invalidita}
                onChange={(e) => setInvalidita(e.target.value)}
                onBlur={() => setInvalidita(payload.percentuale_invalidita)}
                className="input-luxury"
                style={{ backgroundColor: '#0A192F', borderColor: 'rgba(255,255,255,0.12)', color: '#F8F9FA' }}
                data-testid="calc-ip-input"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="calc-morale" style={{ color: '#E2E8F0' }}>
                Personalizzazione / danno morale
              </Label>
              <span className="text-sm font-semibold tabular-nums" style={{ color: '#D4AF37' }}>
                {formatPct(payload.percentuale_danno_morale)}%
              </span>
            </div>
            <Slider
              id="calc-morale"
              min={0}
              max={50}
              step={1}
              value={[payload.percentuale_danno_morale]}
              onValueChange={(v) => setMorale(v[0])}
              className={sliderClass}
              data-testid="calc-morale-slider"
            />
            <p className="text-xs" style={{ color: '#64748B' }}>
              Opzionale. Default 25% (media Milano). Intervallo 0–50%.
            </p>
          </div>

          <div>
            <p className="text-sm font-medium mb-3" style={{ color: '#E2E8F0' }}>
              Inabilità temporanea (giorni)
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <NumberField
                id="calc-itt"
                label="ITT 100%"
                hint="Inabilità totale"
                value={itt}
                onChange={setItt}
                min={0}
                max={3650}
              />
              <NumberField
                id="calc-itp75"
                label="ITP 75%"
                value={itp75}
                onChange={setItp75}
                min={0}
                max={3650}
              />
              <NumberField
                id="calc-itp50"
                label="ITP 50%"
                value={itp50}
                onChange={setItp50}
                min={0}
                max={3650}
              />
              <NumberField
                id="calc-itp25"
                label="ITP 25%"
                value={itp25}
                onChange={setItp25}
                min={0}
                max={3650}
              />
            </div>
          </div>

          {error ? (
            <div
              className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm"
              style={{ color: '#FCA5A5', backgroundColor: 'rgba(127, 29, 29, 0.25)', border: '1px solid rgba(248, 113, 113, 0.35)' }}
              role="alert"
              data-testid="calc-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={isCalculating}
            className="btn-secondary"
            data-testid="calc-submit-btn"
          >
            {isCalculating ? 'Calcolo in corso...' : 'Calcola Risarcimento'}
          </Button>
        </form>
      </div>

      {result ? (
        <div
          className="rounded-xl p-5 md:p-6 space-y-4"
          style={{ backgroundColor: '#112240', border: '1px solid rgba(212, 175, 55, 0.45)' }}
          data-testid="calc-results"
        >
          <h3 className="text-lg font-medium" style={{ fontFamily: 'Playfair Display, serif', color: '#F8F9FA' }}>
            Risultato stima
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ResultCard
              label="Danno biologico permanente base"
              value={formatEuro(result.danno_biologico_permanente_base)}
            />
            <ResultCard
              label={`Incremento sofferenza morale (${formatPct(result.percentuale_danno_morale)}%)`}
              value={formatEuro(result.incremento_sofferenza_morale)}
            />
            <ResultCard
              label="Totale danno temporaneo"
              value={formatEuro(result.danno_biologico_temporaneo)}
              hint={`${result.giorni_itt_equivalenti} giorni ITT equivalenti`}
            />
          </div>
          <div
            className="rounded-lg px-4 py-4"
            style={{ backgroundColor: 'rgba(212, 175, 55, 0.12)', border: '1px solid rgba(212, 175, 55, 0.45)' }}
          >
            <p className="text-xs uppercase tracking-wide" style={{ color: '#A0ABC0' }}>
              Totale generale stimato
            </p>
            <p
              className="text-2xl md:text-3xl font-semibold mt-1 tabular-nums"
              style={{ color: '#D4AF37' }}
              data-testid="calc-totale"
            >
              {formatEuro(result.totale_stimato)}
            </p>
          </div>
          <div className="text-xs space-y-1" style={{ color: '#94A3B8' }}>
            <p>
              ITT 100%: {result.dettaglio_temporaneo?.itt_100?.giorni || 0} gg →{' '}
              {formatEuro(result.dettaglio_temporaneo?.itt_100?.importo)}
            </p>
            <p>
              ITP 75%: {result.dettaglio_temporaneo?.itp_75?.giorni || 0} gg →{' '}
              {formatEuro(result.dettaglio_temporaneo?.itp_75?.importo)}
            </p>
            <p>
              ITP 50%: {result.dettaglio_temporaneo?.itp_50?.giorni || 0} gg →{' '}
              {formatEuro(result.dettaglio_temporaneo?.itp_50?.importo)}
            </p>
            <p>
              ITP 25%: {result.dettaglio_temporaneo?.itp_25?.giorni || 0} gg →{' '}
              {formatEuro(result.dettaglio_temporaneo?.itp_25?.importo)}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={handleCopy}
            className="gap-2"
            style={{ color: copied ? '#22C55E' : '#D4AF37' }}
            data-testid="calc-copy-btn"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Prospetto copiato' : 'Copia Prospetto per Pratica'}
          </Button>
        </div>
      ) : null}

      <p
        className="text-xs md:text-sm rounded-lg px-4 py-3"
        style={{ color: '#CBD5E1', backgroundColor: '#112240', border: '1px solid rgba(255,255,255,0.08)' }}
        data-testid="calc-disclaimer"
      >
        {DISCLAIMER}
      </p>
    </div>
  );
}

function ResultCard({ label, value, hint }) {
  return (
    <div className="rounded-lg p-4" style={{ backgroundColor: '#0A192F', border: '1px solid rgba(255,255,255,0.08)' }}>
      <p className="text-xs uppercase tracking-wide" style={{ color: '#A0ABC0' }}>
        {label}
      </p>
      <p className="text-lg font-semibold mt-1 tabular-nums" style={{ color: '#F8F9FA' }}>
        {value}
      </p>
      {hint ? (
        <p className="text-xs mt-1" style={{ color: '#64748B' }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default DannoBiologicoCalculator;
